import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Progress as Bar,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { unwrap } from '../../api/client';
import { childMetrics, childProgress, childReport, listChildSessions } from '../../api/gen';
import type { Session } from '../../api/gen';
import { ChildAvatar } from '../../components/ChildAvatar';
import { LernfachSection } from '../../components/LernfachSection';
import { ShareCardDialog } from '../../components/ShareCardDialog';
import { dateTime } from '../../lib/text';
import { useOrg, useSession } from '../../lib/session';

// One child's Lernverlauf: the streak and the badge at the top, then the
// written Lernbericht, then every session with what the judge made of it.
//
// The numbers are about effort. "Selbst gelöst" counts sessions the child got
// through themselves; the hint count is how often the ladder had to come down
// a rung. Neither is a grade, and the page never turns them into one.
export function ChildDetailPage() {
  const { id = '' } = useParams();
  const org = useOrg();
  const { pairing } = useSession();
  const child = pairing?.children.find((c) => c.id === id);
  const [sharing, setSharing] = useState(false);

  const progress = useQuery({
    queryKey: ['progress', id],
    queryFn: () => unwrap(childProgress({ path: { childId: id }, headers: org })),
  });
  const metrics = useQuery({
    queryKey: ['metrics', id],
    queryFn: () => unwrap(childMetrics({ path: { childId: id }, headers: org })),
  });
  const report = useQuery({
    queryKey: ['report', id],
    queryFn: () => unwrap(childReport({ path: { childId: id }, headers: org })),
  });
  const sessions = useQuery({
    queryKey: ['sessions', id],
    queryFn: () => unwrap(listChildSessions({ path: { childId: id }, headers: org })),
  });

  if (!child) return <Alert color="red">Dieses Profil gibt es nicht.</Alert>;

  const p = progress.data;
  const m = metrics.data;
  const toNext = p?.solvedToNext ?? 0;

  return (
    <Stack>
      <Group justify="space-between">
        <Group gap="sm">
          <ChildAvatar name={child.name} mode={child.mode} size={48} />
          <div>
            <Title order={3}>{child.name}</Title>
            <Text size="sm" c="dimmed">
              {p?.badge ?? '—'}
            </Text>
          </div>
        </Group>
        <Button variant="light" color="junior" radius="xl" onClick={() => setSharing(true)}>
          Teilen
        </Button>
      </Group>
      <ShareCardDialog
        childId={child.id}
        childName={child.name}
        opened={sharing}
        onClose={() => setSharing(false)}
      />

      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <Stat label="Sitzungen" value={p?.sessions ?? 0} />
        <Stat label="selbst gelöst" value={p?.solved ?? 0} />
        <Stat label="Tage in Folge" value={p?.streak ?? 0} />
        <Stat label="längste Serie" value={p?.bestStreak ?? 0} />
      </SimpleGrid>

      {p?.nextBadge ? (
        <Card withBorder padding="md">
          <Stack gap="xs">
            <Text size="sm">
              Noch {toNext} selbst gelöste Sitzung{toNext === 1 ? '' : 'en'} bis „{p.nextBadge}“
            </Text>
            <Bar value={toNext > 0 ? (100 * (p.solved % (p.solved + toNext))) / (p.solved + toNext) : 100} />
          </Stack>
        </Card>
      ) : null}

      <Card withBorder padding="lg">
        <Stack gap="sm">
          <Group justify="space-between">
            <Title order={5}>Lernbericht</Title>
            {report.data?.updatedAt ? (
              <Text size="xs" c="dimmed">
                {dateTime(report.data.updatedAt)}
              </Text>
            ) : null}
          </Group>
          {report.isLoading ? <Loader size="sm" /> : null}
          {report.data && report.data.basedOnSessions === 0 ? (
            <Text size="sm" c="dimmed">
              Noch nichts bewertet. Der Bericht entsteht, sobald die ersten Sitzungen beurteilt
              sind — nicht vorher, damit er nichts behauptet.
            </Text>
          ) : (
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {report.data?.text}
            </Text>
          )}
        </Stack>
      </Card>

      {m && m.subjects.length > 0 ? (
        <Card withBorder padding="lg">
          <Stack gap="sm">
            <Title order={5}>Nach Fach</Title>
            {m.subjects.map((s) => (
              <Group key={s.subject} justify="space-between">
                <Text size="sm">{s.subject}</Text>
                <Text size="sm" c="dimmed">
                  {s.solved} von {s.sessions} selbst gelöst
                </Text>
              </Group>
            ))}
          </Stack>
        </Card>
      ) : null}

      <LernfachSection childId={child.id} />

      <Title order={5}>Sitzungen</Title>
      {sessions.isLoading ? <Loader size="sm" /> : null}
      {(sessions.data?.sessions ?? []).map((s) => (
        <SessionRow key={s.id} session={s} />
      ))}
      {sessions.data && sessions.data.sessions.length === 0 ? (
        <Text size="sm" c="dimmed">
          Noch keine Sitzung.
        </Text>
      ) : null}
    </Stack>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <Card withBorder padding="md">
      <Text fw={700} size="xl">
        {value}
      </Text>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Card>
  );
}

function SessionRow({ session }: { session: Session }) {
  const evaluation = session.eval;
  return (
    <Card withBorder padding="md">
      <Stack gap={6}>
        <Group justify="space-between" wrap="nowrap">
          <Text fw={600} lineClamp={1}>
            {session.topic || 'Getippte Frage'}
          </Text>
          <Group gap="xs" wrap="nowrap">
            {evaluation ? (
              <Badge variant="light" color={evaluation.solved ? 'green' : 'gray'}>
                {evaluation.solved ? 'selbst gelöst' : 'mit Hilfe'}
              </Badge>
            ) : (
              <Badge variant="light" color="gray">
                {session.status === 'active' ? 'läuft' : 'noch nicht bewertet'}
              </Badge>
            )}
            <Text size="xs" c="dimmed">
              {dateTime(session.createdAt)}
            </Text>
          </Group>
        </Group>

        {evaluation ? (
          <>
            <Text size="sm">{evaluation.summary}</Text>
            <Group gap="md">
              <Text size="xs" c="dimmed">
                {evaluation.subject}
              </Text>
              <Text size="xs" c="dimmed">
                {evaluation.hints} Hinweis{evaluation.hints === 1 ? '' : 'e'}
              </Text>
              {evaluation.struggle ? (
                <Text size="xs" c="dimmed">
                  Hürde: {evaluation.struggle}
                </Text>
              ) : null}
            </Group>
          </>
        ) : null}
      </Stack>
    </Card>
  );
}
